/**
 * Mapa je neorientovaný graf zón — žiadna mriežka. Pohyb, dosah útoku
 * aj zvýrazňovanie v UI stoja na týchto funkciách.
 */

import type { GameMap, MapSpace, SpaceId, ZoneId } from './types.js';

export type Adjacency = Map<SpaceId, SpaceId[]>;

const adjacencyCache = new WeakMap<GameMap, Adjacency>();

export function adjacency(map: GameMap): Adjacency {
  const hit = adjacencyCache.get(map);
  if (hit) return hit;

  const adj: Adjacency = new Map();
  for (const space of map.spaces) adj.set(space.id, []);
  for (const [a, b] of map.edges) {
    const listA = adj.get(a);
    const listB = adj.get(b);
    if (!listA || !listB) {
      throw new Error(`Mapa ${map.id}: hrana ${a}–${b} odkazuje na neexistujúcu zónu`);
    }
    if (!listA.includes(b)) listA.push(b);
    if (!listB.includes(a)) listB.push(a);
  }
  adjacencyCache.set(map, adj);
  return adj;
}

export function neighbors(map: GameMap, spaceId: SpaceId): SpaceId[] {
  return adjacency(map).get(spaceId) ?? [];
}

/**
 * Susedné = spojené čiarou. V jednom priestore stojí najviac jedna figúrka,
 * takže „ten istý priestor" ako cieľ útoku nenastáva.
 */
export function areAdjacent(map: GameMap, a: SpaceId, b: SpaceId): boolean {
  if (a === b) return false;
  return neighbors(map, a).includes(b);
}

const spaceCache = new WeakMap<GameMap, Map<SpaceId, MapSpace>>();

export function spaceById(map: GameMap, id: SpaceId): MapSpace | undefined {
  let index = spaceCache.get(map);
  if (!index) {
    index = new Map(map.spaces.map((s) => [s.id, s]));
    spaceCache.set(map, index);
  }
  return index.get(id);
}

export function zonesOf(map: GameMap, spaceId: SpaceId): ZoneId[] {
  return spaceById(map, spaceId)?.zones ?? [];
}

/** Dva priestory zdieľajú zónu — rozhoduje o dosahu ranged útokov. */
export function shareZone(map: GameMap, a: SpaceId, b: SpaceId): boolean {
  if (a === b) return false;
  const zonesA = zonesOf(map, a);
  return zonesOf(map, b).some((zone) => zonesA.includes(zone));
}

/** Všetky priestory danej zóny — aj tie, ktoré spolu nesusedia. */
export function spacesInZone(map: GameMap, zone: ZoneId): SpaceId[] {
  return map.spaces.filter((s) => s.zones.includes(zone)).map((s) => s.id);
}

export interface ReachResult {
  /** Zóna → počet krokov od štartu (štart má 0). */
  distance: Map<SpaceId, number>;
  /** Zóna → predchodca na najkratšej ceste. */
  from: Map<SpaceId, SpaceId>;
}

/**
 * BFS do vzdialenosti `maxSteps`. Blokovanie zón zatiaľ neexistuje
 * (v Unmatched sa figúrkami prechádza voľne), ale `blocked` je pripravené
 * pre karty, ktoré pohyb obmedzujú.
 */
export function reachFrom(
  map: GameMap,
  start: SpaceId,
  maxSteps: number,
  blocked?: ReadonlySet<SpaceId>,
): ReachResult {
  const distance = new Map<SpaceId, number>([[start, 0]]);
  const from = new Map<SpaceId, SpaceId>();
  if (maxSteps <= 0) return { distance, from };

  let frontier: SpaceId[] = [start];
  for (let step = 1; step <= maxSteps && frontier.length > 0; step++) {
    const next: SpaceId[] = [];
    for (const current of frontier) {
      for (const neighbor of neighbors(map, current)) {
        if (distance.has(neighbor)) continue;
        if (blocked?.has(neighbor)) continue;
        distance.set(neighbor, step);
        from.set(neighbor, current);
        next.push(neighbor);
      }
    }
    frontier = next;
  }
  return { distance, from };
}

/** Zóny dosiahnuteľné na `maxSteps` krokov, bez štartovej zóny. */
export function reachableSpaces(
  map: GameMap,
  start: SpaceId,
  maxSteps: number,
  blocked?: ReadonlySet<SpaceId>,
): SpaceId[] {
  const { distance } = reachFrom(map, start, maxSteps, blocked);
  const out: SpaceId[] = [];
  for (const [spaceId] of distance) if (spaceId !== start) out.push(spaceId);
  return out;
}

/**
 * Cesty ku všetkým dosiahnuteľným zónam z jedného BFS.
 * Lacnejšie než volať `shortestPath` pre každý cieľ zvlášť.
 */
export function pathsFrom(
  map: GameMap,
  start: SpaceId,
  maxSteps: number,
  blocked?: ReadonlySet<SpaceId>,
): Map<SpaceId, SpaceId[]> {
  const { distance, from } = reachFrom(map, start, maxSteps, blocked);
  const paths = new Map<SpaceId, SpaceId[]>([[start, [start]]]);

  // Zóny v poradí podľa vzdialenosti — predchodca má cestu už hotovú.
  const ordered = [...distance.entries()].sort((a, b) => a[1] - b[1]);
  for (const [spaceId] of ordered) {
    if (spaceId === start) continue;
    const prev = from.get(spaceId);
    const prefix = prev ? paths.get(prev) : undefined;
    if (prefix) paths.set(spaceId, [...prefix, spaceId]);
  }
  return paths;
}

/** Najkratšia cesta vrátane štartu a cieľa, alebo null ak je cieľ mimo dosahu. */
export function shortestPath(
  map: GameMap,
  start: SpaceId,
  target: SpaceId,
  maxSteps: number,
  blocked?: ReadonlySet<SpaceId>,
): SpaceId[] | null {
  if (start === target) return [start];
  const { distance, from } = reachFrom(map, start, maxSteps, blocked);
  if (!distance.has(target)) return null;

  const path: SpaceId[] = [target];
  let cursor = target;
  while (cursor !== start) {
    const prev = from.get(cursor);
    if (!prev) return null;
    path.push(prev);
    cursor = prev;
  }
  return path.reverse();
}

/** Overí, že cesta je súvislá postupnosť susediacich zón v rámci limitu. */
export function isValidPath(
  map: GameMap,
  path: readonly SpaceId[],
  start: SpaceId,
  maxSteps: number,
): boolean {
  if (path.length === 0 || path[0] !== start) return false;
  if (path.length - 1 > maxSteps) return false;
  for (let i = 1; i < path.length; i++) {
    const prev = path[i - 1] as SpaceId;
    const current = path[i] as SpaceId;
    if (!neighbors(map, prev).includes(current)) return false;
  }
  return true;
}
